import React, { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import Cookies from 'js-cookie'
import { getBREDocumentData } from './common/institutionalServiceHelper.js'
import { getRelativeBasePath } from '../../../global/environments/path'
import FormNotification from '../../../components/SelfService/FormNotification.jsx'
import InputGroup from '../../../components/EmailSubscriptionCenter/SignIn/InputGroup.jsx'
import { accessStorage } from './common/institutionalConfig.js'
import {
  regexValidationForUserId,
  isSsnFormat,
} from '../../../global/_utils.js'
import {
  makeLoginRequest,
  fetchToken,
  errorMessage,
  makeRiskAdviseRequest,
  LOGIN_RESPONSE_CODES,
  screens,
} from './common/institutionalServiceHelper.js'

export default function InstitutionalLoginScreen(props) {
  const {
    screenChangeHandler,
    setShowLoader,
    onSuccessFullLogin,
    isTempChangePassword,
    tmxId,
  } = props
  const [bannerData, setBannerData] = useState()
  const [userId, setUserId] = useState(accessStorage.getUserId())
  const [password, setPassword] = useState('')
  const [rememberLogin, setRememberLogin] = useState(
    !!accessStorage.getUserId()
  )
  const [showPasswordField, setShowPasswordField] = useState(false)
  const [disableAllInputs, setDisableAllInputs] = useState(false)
  const [formErrorMessage, setFormErrorMessage] = useState('')
  const [formValidation, setFormValidation] = useState({
    'login-user-id': {
      isValid: false,
      error: false,
      errorMessage: '',
    },
    'login-user-password': {
      isValid: false,
      error: false,
      errorMessage: 'Enter password',
    },
  })

  const resetError = () => {
    const _formValidation = { ...formValidation }
    _formValidation['login-user-id'].error = false
    _formValidation['login-user-password'].error = false
    setFormValidation(_formValidation)
  }

  const handleLoginInputChange = (e) => {
    setUserId(e.currentTarget.value)
    resetError()
  }

  const handlePasswordInputChange = (e) => {
    setPassword(e?.target?.value?.replace(/[\\"]/g, ''))
    resetError()
  }

  const onRemeberLoginChecked = (e) => {
    setRememberLogin(e.target.checked)
  }

  const userIdValidation = () => {
    const isValidUserId = regexValidationForUserId(userId)
    const _formValidation = { ...formValidation }
    _formValidation['login-user-id'].isValid = false
    if (!userId) {
      _formValidation['login-user-id'].error = true
      _formValidation['login-user-id'].errorMessage = 'Enter user id'
    } else if (isSsnFormat(userId)) {
      _formValidation['login-user-id'].error = true
      _formValidation['login-user-id'].errorMessage =
        'The User ID you entered is incorrect. Please check and try again.'
    } else if (!isValidUserId?.isValid) {
      _formValidation['login-user-id'].error = true
      _formValidation['login-user-id'].errorMessage = isValidUserId?.message
    } else {
      _formValidation['login-user-id'].isValid = true
    }
    setFormValidation(_formValidation)
  }

  const onPasswordPaste = () => {
    e.preventDefault()
    const text = e.clipboardData.getData('text').replace(/[\\"]/g, '')
    const target = e.target
    const { selectionStart, selectionEnd } = target
    const next =
      password?.slice(0, selectionStart) + text + password?.slice(selectionEnd)
    setPassword(next)
  }

  const passwordValidation = () => {
    const _formValidation = { ...formValidation }
    _formValidation['login-user-password'].isValid = false
    if (!password) {
      _formValidation['login-user-password'].error = true
      _formValidation['login-user-password'].errorMessage = 'Enter password'
    } else {
      _formValidation['login-user-password'].isValid = true
    }
    setFormValidation(_formValidation)
  }

  const handleNextClicked = () => {
    userIdValidation()
    if (formValidation['login-user-id'].isValid) {
      setShowPasswordField(true)
    }
  }

  const handleLoginClicked = async () => {
    userIdValidation()
    passwordValidation()
    if (
      formValidation['login-user-id'].isValid &&
      formValidation['login-user-password'].isValid
    ) {
      Cookies.remove('CSMSESSION')
      accessStorage.setUserId(rememberLogin ? userId : null)
      makeLoginApiCall()
    }
  }

  const makeLoginApiCall = async () => {
    setShowLoader(true)
    try {
      await fetchToken(tmxId)
      const data = await makeLoginRequest({
        userId,
        password,
        deviceSignature: window?.getDeviceSignature(),
      })

      if (
        data?.value?.userInfo &&
        Object.keys(data?.value?.userInfo)?.length > 1
      ) {
        accessStorage.setUserInfo({
          ...data?.value?.userInfo,
          userId: userId,
        })
      }
      handleSuccessLogin(data)
    } catch (error) {
      setFormErrorMessage(errorMessage.defaultError)
    }
    setShowLoader(false)
  }

  const tempPasswordRiskAdvice = async () => {
    setShowLoader(true)
    try {
      const data = await makeRiskAdviseRequest(null, {
        action: 'changeTempPwd',
      })
      if (
        data?.value?.userInfo &&
        Object.keys(data?.value?.userInfo)?.length > 1
      ) {
        accessStorage.setUserInfo({
          ...data?.value?.userInfo,
          userId: userId,
        })
      }
      handleSuccessLogin(data)
    } catch (error) {
      setFormErrorMessage(errorMessage.defaultError)
    }
    setShowLoader(false)
  }

  const handleSuccessLogin = (data) => {
    if (data.value?.phnVrfStatus === 'INACTIVE') {
      handleState(data.value.phnVrfStatus, data)
    } else if (data?.value?.riskAdvice) {
      if (data.value.riskAdvice === 'INCREASEAUTH' && data?.value?.reasonCode) {
        handleState(data.value.reasonCode, data)
      } else {
        handleState(data.value.riskAdvice, data)
      }
    } else if (data?.value?.reasonCode) {
      handleState(data.value.reasonCode, data)
    } else if (data?.error?.details) {
      handleState(data.error.details.code, data)
    } else {
      setFormErrorMessage(errorMessage.defaultError)
    }
  }

  const handleState = (code, data) => {
    switch (code) {
      case LOGIN_RESPONSE_CODES.TEMP_PASSWORD_INCREASE_AUTH:
      case LOGIN_RESPONSE_CODES.MFA_CHOICE:
      case LOGIN_RESPONSE_CODES.MFA_CHOICE_2:
        screenChangeHandler(screens.MFA_SCREEN, {
          mfaData: data?.value?.routes?.value,
        })
        break
      case LOGIN_RESPONSE_CODES.SUCCESS_LOGIN:
        onSuccessFullLogin()
        break
      case LOGIN_RESPONSE_CODES.TEMPORARY_PASSWORD:
        screenChangeHandler(null, { isTempChangePassword: true })
        break
      case LOGIN_RESPONSE_CODES.CREDENTIALS_NOT_ACCEPTED:
        setFormErrorMessage(errorMessage.userIdAndPasswordIncorrect)
        break
      case LOGIN_RESPONSE_CODES.TEMP_PASSWORD_EXPIRED:
        setFormErrorMessage(errorMessage.tempPasswordExpired)
        break
      case LOGIN_RESPONSE_CODES.SSO_LOCKED:
      case LOGIN_RESPONSE_CODES.SSO_LOCKED_2:
      case LOGIN_RESPONSE_CODES.BLOCKED_MFA:
      case LOGIN_RESPONSE_CODES.LOCKED_MFA:
        setFormErrorMessage(errorMessage.accountLocked)
        setDisableAllInputs(true)
        break
      case LOGIN_RESPONSE_CODES.CONTACT_RM:
        setFormErrorMessage(errorMessage.contactRM)
        break
      default:
        setFormErrorMessage(errorMessage.defaultError)
    }
  }

  const handlePasswordInputOnKeyUp = (event) => {
    if (event.key === 'Enter') {
      handleLoginClicked()
    }
  }

  useEffect(() => {
    if (isTempChangePassword) {
      tempPasswordRiskAdvice()
    }
  }, [isTempChangePassword])

  const getBREData = async () => {
    const data = await getBREDocumentData('login/security-update-banner')
    setBannerData({
      title: data[0]?.title,
      content: data[0]?.html?.content,
    })
  }

  useEffect(() => {
    getBREData()
  }, [])

  return (
    <div data-analytics-login-step="Log In" className="u-sizeConstrained">
      <div className="Grid">
        <div className="u-sizeFull u-lg-size12of12 u-marginBottom6gu">
          <h2 className="u-marginBottom2gu u-marginTop0">
            {bannerData?.title}
          </h2>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bannerData?.content),
            }}
          ></div>
        </div>
      </div>
      <div className="Grid">
        <div className="u-sizeFull u-lg-size5of12 u-lg-after1of12 u-marginBottom6gu Content">
          {formErrorMessage && (
            <FormNotification content={formErrorMessage} isHtml={true} />
          )}
          <h2 className="u-marginBottom2gu u-marginTop0">Log In</h2>
          <p className="u-marginBottom3gu">
            Not an institutional investor? Log in as a{' '}
            <a href="/login">financial professional</a> or{' '}
            <a href="/web/columbia/landing/account-access/login">
              individual investor
            </a>
          </p>

          <fieldset>
            <InputGroup
              handleInputChange={handleLoginInputChange}
              formValidation={formValidation}
              fieldName="login-user-id"
              fieldId="login-user-id"
              label="User ID"
              placeholder="User Id"
              compact={false}
              disabled={disableAllInputs}
              value={userId}
            />

            {showPasswordField && (
              <InputGroup
                onPaste={onPasswordPaste}
                type="password"
                handleInputChange={handlePasswordInputChange}
                formValidation={formValidation}
                fieldName="login-user-password"
                fieldId="login-user-password"
                label="User password"
                placeholder="Password"
                compact={false}
                disabled={disableAllInputs}
                defaultValue={password}
                maskedValue={password}
                onKeyUp={handlePasswordInputOnKeyUp}
              />
            )}

            {!showPasswordField && (
              <button
                onClick={handleNextClicked}
                className="Button Button--primary Button--block u-marginBottom4gu u-textNoTransform"
                data-analytics="login-next"
              >
                Next
              </button>
            )}

            {showPasswordField && (
              <button
                disabled={disableAllInputs}
                onClick={handleLoginClicked}
                className="Button Button--primary Button--block u-marginBottom4gu"
                data-analytics="login"
              >
                Log in
              </button>
            )}

            <div className="Checkbox u-marginBottom3gu">
              <input
                defaultChecked={rememberLogin}
                onChange={onRemeberLoginChecked}
                disabled={disableAllInputs}
                className="Checkbox-button u-bgColorWhite"
                id="remember-user-id"
                type="checkbox"
              />
              <label htmlFor="remember-user-id" className="Checkbox-label">
                Remember User ID
              </label>
            </div>
            <a
              href={`${getRelativeBasePath()}/identity-proofing`}
              className="Link"
              data-analytics="login-forgot-password"
            >
              Forgot password?
            </a>
          </fieldset>
        </div>
      </div>
    </div>
  )
}
